import React, { useState, useRef } from 'react';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import Input from 'components/ui/Input';

const STATUS_OPTIONS = ['En Espera', 'Procesando', 'Listo para Venta', 'Completado', 'Cancelado'];

function FieldError({ msg }) {
  if (!msg) return null;
  return (
    <p className="text-xs mt-1 text-red-500" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
      {msg}
    </p>
  );
}

function validate(form) {
  const errors = {};
  const yieldEst = parseFloat(form?.yieldEstimated);
  if (form?.yieldEstimated !== '' && form?.yieldEstimated != null) {
    if (isNaN(yieldEst) || yieldEst < 0 || yieldEst > 100)
      errors.yieldEstimated = 'Debe estar entre 0 y 100';
  }
  const yieldActual = parseFloat(form?.yieldActual);
  if (form?.yieldActual !== '' && form?.yieldActual != null) {
    if (isNaN(yieldActual) || yieldActual < 0 || yieldActual > 100)
      errors.yieldActual = 'Debe estar entre 0 y 100';
  }
  const logistics = parseFloat(form?.logisticsCost);
  if (form?.logisticsCost !== '' && form?.logisticsCost != null) {
    if (isNaN(logistics) || logistics < 0)
      errors.logisticsCost = 'Debe ser un valor positivo (≥ 0)';
  }
  const progress = parseInt(form?.progress);
  if (isNaN(progress) || progress < 0 || progress > 100)
    errors.progress = 'Debe estar entre 0 y 100';
  return errors;
}

export default function EditBatchModal({ batch, onClose, onSave }) {
  const [form, setForm] = useState({
    yieldEstimated: batch?.yieldEstimated,
    yieldActual: batch?.yieldActual || '',
    logisticsCost: batch?.logisticsCost,
    notes: batch?.notes || '',
    status: batch?.status,
    progress: batch?.progress,
  });
  const [errors, setErrors] = useState({});
  const [shake, setShake] = useState(false);
  const btnRef = useRef(null);

  const hasErrors = Object.keys(errors)?.length > 0;

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    // Clear error for this field on change
    setErrors(prev => {
      const next = { ...prev };
      delete next?.[field];
      return next;
    });
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    const newErrors = validate(form);
    if (Object.keys(newErrors)?.length > 0) {
      setErrors(newErrors);
      // Trigger shake
      setShake(true);
      setTimeout(() => setShake(false), 500);
      return;
    }
    setErrors({});
    onSave({ ...batch, ...form });
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <h2 className="text-lg font-bold text-slate-800" style={{ fontFamily: '"Outfit", sans-serif' }}>
            Editar Lote {batch?.batchId}
          </h2>
          <button onClick={onClose} className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
            <Icon name="X" size={18} color="currentColor" />
          </button>
        </div>

        <style>{`
          @keyframes shake {
            0%, 100% { transform: translateX(0); }
            15% { transform: translateX(-6px); }
            30% { transform: translateX(6px); }
            45% { transform: translateX(-4px); }
            60% { transform: translateX(4px); }
            75% { transform: translateX(-2px); }
            90% { transform: translateX(2px); }
          }
          .shake-anim { animation: shake 0.5s ease-in-out; }
        `}</style>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Input
                label="Yield Estimado (%)"
                type="number"
                value={form?.yieldEstimated}
                onChange={e => handleChange('yieldEstimated', parseFloat(e?.target?.value))}
                min="0" max="100" step="0.1"
                placeholder="92.0"
                style={errors?.yieldEstimated ? { borderColor: '#ef4444' } : {}}
              />
              <FieldError msg={errors?.yieldEstimated} />
            </div>
            <div>
              <Input
                label="Yield Real (%)"
                type="number"
                value={form?.yieldActual}
                onChange={e => handleChange('yieldActual', e?.target?.value ? parseFloat(e?.target?.value) : '')}
                min="0" max="100" step="0.1"
                placeholder="—"
                style={errors?.yieldActual ? { borderColor: '#ef4444' } : {}}
              />
              <FieldError msg={errors?.yieldActual} />
            </div>
          </div>

          <div>
            <Input
              label="Coste Logística (€)"
              type="number"
              value={form?.logisticsCost}
              onChange={e => handleChange('logisticsCost', parseFloat(e?.target?.value))}
              min="0" step="any"
              placeholder="500"
              style={errors?.logisticsCost ? { borderColor: '#ef4444' } : {}}
            />
            <FieldError msg={errors?.logisticsCost} />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
              Estado
            </label>
            <select
              value={form?.status}
              onChange={e => handleChange('status', e?.target?.value)}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-orange-500"
              style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}
            >
              {STATUS_OPTIONS?.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
              Progreso (%)
            </label>
            <input
              type="range"
              min="0" max="100" step="5"
              value={form?.progress}
              onChange={e => handleChange('progress', parseInt(e?.target?.value))}
              className="w-full accent-orange-600"
            />
            <div className="flex justify-between text-xs text-slate-400 mt-1" style={{ fontFamily: '"JetBrains Mono", monospace' }}>
              <span>0%</span><span className="font-semibold text-orange-600">{form?.progress}%</span><span>100%</span>
            </div>
            <FieldError msg={errors?.progress} />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
              Notas
            </label>
            <textarea
              value={form?.notes}
              onChange={e => handleChange('notes', e?.target?.value)}
              rows={3}
              placeholder="Observaciones sobre el lote..."
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
              style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}
            />
          </div>

          <div className="flex items-center justify-end gap-3 pt-2">
            <Button variant="outline" size="sm" onClick={onClose} type="button">Cancelar</Button>
            <div ref={btnRef} className={shake ? 'shake-anim' : ''}>
              <Button
                variant="default"
                size="sm"
                type="submit"
                iconName="Save"
                iconPosition="left"
                disabled={hasErrors}
              >
                Guardar Cambios
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}